import React, { useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import {
  PlusIcon,
  EyeIcon,
  PencilSquareIcon,
  PauseCircleIcon,
  PlayCircleIcon,
  ClipboardDocumentListIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import TableComponent from '../TableComponent';
import CustomChip from '../chips/CustomChip';
import RowActionMenu from '../menu/RowActionMenu';
import { InputBox, Selectbox } from '../FormComponent';
import ConfirmationModal from '../Modal/ConfirmationModal';
import { getSchedulesForGroup, pauseSchedule, resumeSchedule, deleteSchedule } from '../../utils/api/schedule_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { useSnackbar } from '../../utils/useSnackbar';
import { dateFormat } from '../../utils/date';
import {
  formatRunTime12Hour,
  formatScheduleCadence,
  getScheduleStatusChipColors,
  getScheduleCadenceChipColors,
  getScheduleFireOutcomeChipColors,
  getScheduleFireOutcomeLabel,
  getScheduleLastFireRowTone,
} from '../../utils/scheduleDisplay';
import TestGroupSchedulesEmptyPanel from './TestGroupSchedulesEmptyPanel';
import moment from 'moment';
import styles from '../../styles/launchesTable/index.module.scss';

const {
  LaunchType,
  LaunchTypeLabel,
  ScheduleCadence,
  ScheduleStatus,
  ScheduleFireOutcome,
} = require('../../enums/ScheduleEnums');

const DEFAULT_FILTERS = {
  search: '',
  launchType: 'all',
  environmentName: 'all',
  cadence: 'all',
  lastFire: 'all',
};

const LAST_FIRE_FILTER_OPTIONS = [
  { value: 'all', label: 'All last fire' },
  { value: 'none', label: 'No fires yet' },
  { value: ScheduleFireOutcome.FIRED, label: 'Fired' },
  { value: 'skipped', label: 'Any skip' },
  { value: 'blocked', label: 'Any block / failure' },
  { value: ScheduleFireOutcome.SKIPPED_SAME_DAY_GUARD, label: 'Skipped (same day)' },
  { value: ScheduleFireOutcome.SKIPPED_GATE_BLOCKED, label: 'Skipped (gate blocked)' },
  { value: ScheduleFireOutcome.SKIPPED_EMPTY_GROUP, label: 'Skipped (empty group)' },
  { value: ScheduleFireOutcome.SKIPPED_NO_CASES_FOR_TYPE, label: 'Skipped (no cases for type)' },
  { value: ScheduleFireOutcome.GUARD_FAILED_CREDITS, label: 'Blocked (credits)' },
  { value: ScheduleFireOutcome.GUARD_FAILED_TRIAL, label: 'Blocked (trial)' },
  { value: ScheduleFireOutcome.GUARD_FAILED_ENV_REMOVED, label: 'Blocked (environment removed)' },
  { value: ScheduleFireOutcome.GUARD_FAILED_TARGET_DELETED, label: 'Blocked (group deleted)' },
  { value: ScheduleFireOutcome.DISPATCH_FAILED, label: 'Dispatch failed' },
  { value: ScheduleFireOutcome.WORKER_ERROR, label: 'Worker error' },
];

function matchesLastFireFilter(outcome, filter) {
  if (filter === 'all') return true;
  if (filter === 'none') return !outcome;
  if (filter === 'skipped') return Boolean(outcome) && String(outcome).startsWith('SKIPPED_');
  if (filter === 'blocked') {
    return Boolean(outcome)
      && (String(outcome).startsWith('GUARD_')
        || outcome === ScheduleFireOutcome.DISPATCH_FAILED
        || outcome === ScheduleFireOutcome.WORKER_ERROR);
  }
  return outcome === filter;
}

const SchedulesTable = forwardRef(({
  groupId,
  onCreateSchedule,
  onEditSchedule,
  onViewSchedule,
  onViewChangeLog,
}, ref) => {
  const theme = useTheme();
  const palette = theme.palette.customColor;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [mutatingId, setMutatingId] = useState(null);
  const { showSnackbar } = useSnackbar();
  const isEmpty = !loading && rows.length === 0;
  const hasDefaultFilters =
    (filters.search ?? '') === ''
    && filters.launchType === 'all'
    && filters.environmentName === 'all'
    && filters.cadence === 'all'
    && filters.lastFire === 'all';

  const fetchSchedules = async () => {
    if (!groupId) return;
    setLoading(true);
    try {
      const res = await getSchedulesForGroup({ testGroupId: groupId });
      if (res.status === STATUS.SUCCESS) {
        setRows((res.data?.rows || []).map((r) => ({
          id: r.id,
          name: r.name,
          launch_type: r.launch_type,
          environment_name: r.environment?.name || null,
          status: r.status,
          cadence: r.cadence,
          recurrence_day: r.recurrence_day,
          run_time: r.run_time,
          next_run_at: r.next_run_at,
          end_date: r.end_date,
          last_fire_outcome: r.last_fire_outcome || null,
          last_fire_case_count: r.last_fire_case_count ?? null,
        })));
      } else {
        setRows([]);
        if (res.error) showSnackbar(res.error, 'error');
      }
    } catch (err) {
      setRows([]);
      showSnackbar(err?.message || 'Failed to load schedules', 'error');
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({ refresh: fetchSchedules }), [groupId]);

  useEffect(() => {
    fetchSchedules();
  }, [groupId]);

  const environmentFilterOptions = useMemo(() => {
    const names = [...new Set(rows.map((r) => r.environment_name).filter(Boolean))].sort();
    return [
      { value: 'all', label: 'All Environments' },
      ...names.map((name) => ({ value: name, label: name })),
    ];
  }, [rows]);

  const filteredRows = useMemo(() => rows.filter((row) => {
    const search = (filters.search || '').trim().toLowerCase();
    if (search && !(row.name || '').toLowerCase().includes(search)) {
      return false;
    }
    if (filters.launchType !== 'all' && row.launch_type !== filters.launchType) {
      return false;
    }
    if (filters.environmentName !== 'all' && row.environment_name !== filters.environmentName) {
      return false;
    }
    if (filters.cadence !== 'all' && row.cadence !== filters.cadence) {
      return false;
    }
    if (!matchesLastFireFilter(row.last_fire_outcome, filters.lastFire)) {
      return false;
    }
    return true;
  }), [rows, filters]);

  const isFilteredEmpty = !loading && rows.length > 0 && filteredRows.length === 0;

  const handlePause = async (schedule) => {
    if (mutatingId) return;
    setMutatingId(schedule.id);
    const res = await pauseSchedule({ scheduleId: schedule.id });
    setMutatingId(null);
    if (res.status === STATUS.SUCCESS) {
      showSnackbar('Schedule paused', 'success');
      fetchSchedules();
    } else {
      showSnackbar(res.error || 'Failed to pause schedule', 'error');
    }
  };

  const handleResume = async (schedule) => {
    if (mutatingId) return;
    setMutatingId(schedule.id);
    const res = await resumeSchedule({ scheduleId: schedule.id });
    setMutatingId(null);
    if (res.status === STATUS.SUCCESS) {
      showSnackbar('Schedule resumed', 'success');
      fetchSchedules();
      return;
    }
    const code = res.code;
    if (code === 'TRIAL_EXPIRED') {
      showSnackbar("Can't resume yet: your trial period has expired. Upgrade your plan, then resume.", 'error');
      return;
    }
    if (code === 'INSUFFICIENT_CREDITS') {
      showSnackbar("Can't resume yet: the organization is out of execution credits. Add credits, then resume.", 'error');
      return;
    }
    if (code === 'ENVIRONMENT_REMOVED') {
      showSnackbar(
        res.error
          || "Can't resume yet: the environment this schedule uses was removed. Edit the schedule to select an available environment, then resume.",
        'error'
      );
      return;
    }
    if (code === 'TARGET_GROUP_DELETED') {
      showSnackbar(
        res.error
          || "Can't resume yet: the target test group was deleted. This schedule can't run without it.",
        'error'
      );
      return;
    }
    if (code === 'EMPTY_GROUP') {
      showSnackbar(
        res.error
          || "Can't resume yet: this group has no test cases. Add tests, then resume.",
        'error'
      );
      return;
    }
    if (code === 'NO_CASES_FOR_TYPE') {
      showSnackbar(
        res.error
          || "Can't resume yet: this group no longer has any tests matching this schedule's type. Add matching tests, or edit the schedule's type, then resume.",
        'error'
      );
      return;
    }
    if (code === 'SCHEDULE_EXPIRED') {
      showSnackbar(
        res.error
          || 'This schedule has expired. Edit it and set a new end date in the future to activate it again.',
        'error'
      );
      return;
    }
    showSnackbar(res.error || res.message || 'Failed to resume schedule', 'error');
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      const res = await deleteSchedule({ scheduleId: deleteTarget.id });
      if (res.status === STATUS.SUCCESS) {
        showSnackbar(res.message || 'Schedule deleted', 'success');
        setDeleteTarget(null);
        fetchSchedules();
      } else {
        showSnackbar(res.error || res.message || 'Failed to delete schedule', 'error');
      }
    } finally {
      setDeleting(false);
    }
  };

  const getRowActionButtons = (row) => {
    const items = [
      {
        name: 'View',
        icon: <EyeIcon className="icon" />,
        action: () => onViewSchedule?.(row),
      },
      {
        name: 'Change log',
        icon: <ClipboardDocumentListIcon className="icon" />,
        action: () => onViewChangeLog?.(row),
      },
      {
        name: 'Edit',
        icon: <PencilSquareIcon className="icon" />,
        action: () => onEditSchedule?.(row),
      },
    ];

    if (row.status === ScheduleStatus.ACTIVE) {
      items.push({
        name: 'Pause',
        icon: <PauseCircleIcon className="icon" />,
        action: () => handlePause(row),
        disabled: () => mutatingId === row.id,
      });
    } else if (
      row.status === ScheduleStatus.PAUSED
      || row.status === ScheduleStatus.AUTO_PAUSED
    ) {
      items.push({
        name: 'Resume',
        icon: <PlayCircleIcon className="icon" />,
        action: () => handleResume(row),
        disabled: () => mutatingId === row.id,
      });
    }

    items.push({
      name: 'Delete',
      icon: <TrashIcon className="icon" />,
      action: () => setDeleteTarget(row),
    });

    return items;
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.2,
      minWidth: 160,
      renderCell: (params) => (
        <button
          type="button"
          className={styles['schedule-name-link']}
          title={params.value || ''}
          onClick={() => onViewSchedule?.(params.row)}
        >
          {params.value}
        </button>
      ),
    },
    {
      field: 'launch_type',
      headerName: 'Type',
      flex: 0.7,
      minWidth: 100,
      renderCell: (params) => {
        const type = params.value || LaunchType.COMPLETE;
        const label = LaunchTypeLabel[type] || type;
        const isAutomated = type === LaunchType.AUTOMATED;
        const isManual = type === LaunchType.MANUAL;
        return (
          <CustomChip
            label={label}
            color={isAutomated ? palette.blue[50] : isManual ? palette.slate[50] : palette.green[50]}
            textColor={isAutomated ? palette.blue[600] : isManual ? palette.slate[700] : palette.green[600]}
            borderColor={isAutomated ? palette.blue[200] : isManual ? palette.slate[200] : palette.green[200]}
            size="small"
          />
        );
      },
    },
    {
      field: 'environment_name',
      headerName: 'Environment',
      flex: 0.9,
      minWidth: 110,
      renderCell: (params) => {
        const text = params.value || '—';
        return (
          <span className={styles['schedule-cell-ellipsis']} title={text === '—' ? undefined : text}>
            {text}
          </span>
        );
      },
    },
    {
      field: 'cadence',
      headerName: 'Cadence',
      flex: 0.9,
      minWidth: 120,
      renderCell: (params) => {
        const cadence = params.row.cadence;
        if (!cadence) return '—';
        const full = formatScheduleCadence(params.row);
        if (!full || full === '—') return '—';
        const parenStart = full.indexOf(' (');
        const cadenceLabel = parenStart > 0 ? full.slice(0, parenStart) : full;
        const detail = parenStart > 0 ? full.slice(parenStart) : '';
        const colors = getScheduleCadenceChipColors(palette, cadence);
        return (
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'nowrap', maxWidth: '100%', overflow: 'hidden' }}
            title={full}
          >
            <CustomChip
              label={cadenceLabel}
              color={colors.color}
              textColor={colors.textColor}
              borderColor={colors.borderColor}
              size="small"
            />
            {detail ? (
              <Box
                component="span"
                className={styles['schedule-cell-ellipsis']}
                sx={{ color: 'text.secondary', fontSize: '0.8125rem', flex: 1, minWidth: 0 }}
              >
                {detail.trim()}
              </Box>
            ) : null}
          </Box>
        );
      },
    },
    {
      field: 'run_time',
      headerName: 'Run time',
      flex: 0.6,
      minWidth: 90,
      renderCell: (params) => {
        const text = formatRunTime12Hour(params.value);
        return (
          <span className={styles['schedule-cell-ellipsis']} title={text && text !== '—' ? text : undefined}>
            {text}
          </span>
        );
      },
    },
    {
      field: 'next_run_at',
      headerName: 'Next run',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => {
        const text = params.row.status === ScheduleStatus.ACTIVE && params.value
          ? dateFormat(params.value)
          : '—';
        return (
          <span className={styles['schedule-cell-ellipsis']} title={text === '—' ? undefined : text}>
            {text}
          </span>
        );
      },
    },
    {
      field: 'end_date',
      headerName: 'Ends',
      flex: 0.7,
      minWidth: 100,
      renderCell: (params) => {
        const text = params.value ? moment(params.value).format('MMM D, YYYY') : '—';
        return (
          <span className={styles['schedule-cell-ellipsis']} title={text === '—' ? undefined : text}>
            {text}
          </span>
        );
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 110,
      renderCell: (params) => {
        const chipColors = getScheduleStatusChipColors(palette, params.value);
        const label = String(params.value || '').replace(/_/g, ' ');
        return (
          <CustomChip
            label={label || '—'}
            color={chipColors.color}
            textColor={chipColors.textColor}
            borderColor={chipColors.borderColor}
            size="small"
          />
        );
      },
    },
    {
      field: 'last_fire_outcome',
      headerName: 'Last fire',
      flex: 1.1,
      minWidth: 150,
      renderCell: (params) => {
        if (!params.value) return '—';
        const label = getScheduleFireOutcomeLabel(params.value);
        const colors = getScheduleFireOutcomeChipColors(palette, params.value);
        const caseCount = params.row.last_fire_case_count;
        const chipLabel = caseCount != null
          ? `${label} · ${caseCount} ${caseCount === 1 ? 'case' : 'cases'}`
          : label;
        return (
          <CustomChip
            label={chipLabel}
            color={colors.color}
            textColor={colors.textColor}
            borderColor={colors.borderColor}
            size="small"
          />
        );
      },
    },
    {
      field: 'Actions',
      headerName: 'Actions',
      hideable: false,
      minWidth: 55,
      maxWidth: 90,
      resizable: false,
      disableColumnMenu: true,
      flex: 0,
      headerAlign: 'center',
      sortable: false,
      align: 'center',
      filterable: false,
      renderCell: (params) => (
        <RowActionMenu
          menuItems={getRowActionButtons(params.row)}
          rowData={params.row}
          isDisabled={false}
        />
      ),
    },
  ];

  return (
    <Box className={styles['launches-table-container']}>
      {!(isEmpty && hasDefaultFilters) && (
        <Box className={styles['filter-section']}>
          <InputBox
            placeholder="Search schedules..."
            value={filters.search || ''}
            onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
            className={styles['search-input']}
            name="search"
            label="Search schedule"
          />
          <Selectbox
            value={filters.launchType || 'all'}
            disableClearable={(filters.launchType || 'all') === 'all'}
            onChange={(e) => setFilters((prev) => ({ ...prev, launchType: e.target.value || 'all' }))}
            className={styles['schedule-filter-select']}
            name="launchType"
            options={[
              { value: 'all', label: 'All Types' },
              { value: LaunchType.AUTOMATED, label: LaunchTypeLabel[LaunchType.AUTOMATED] },
              { value: LaunchType.MANUAL, label: LaunchTypeLabel[LaunchType.MANUAL] },
              { value: LaunchType.COMPLETE, label: LaunchTypeLabel[LaunchType.COMPLETE] },
            ]}
          />
          <Selectbox
            value={filters.environmentName || 'all'}
            disableClearable={(filters.environmentName || 'all') === 'all'}
            onChange={(e) => setFilters((prev) => ({ ...prev, environmentName: e.target.value || 'all' }))}
            className={styles['schedule-filter-select']}
            name="environment"
            options={environmentFilterOptions}
          />
          <Selectbox
            value={filters.cadence || 'all'}
            disableClearable={(filters.cadence || 'all') === 'all'}
            onChange={(e) => setFilters((prev) => ({ ...prev, cadence: e.target.value || 'all' }))}
            className={styles['schedule-filter-select']}
            name="cadence"
            options={[
              { value: 'all', label: 'All Cadences' },
              { value: ScheduleCadence.DAILY, label: 'Daily' },
              { value: ScheduleCadence.WEEKLY, label: 'Weekly' },
              { value: ScheduleCadence.MONTHLY, label: 'Monthly' },
            ]}
          />
          <Selectbox
            value={filters.lastFire || 'all'}
            disableClearable={(filters.lastFire || 'all') === 'all'}
            onChange={(e) => setFilters((prev) => ({ ...prev, lastFire: e.target.value || 'all' }))}
            className={styles['schedule-filter-select']}
            name="lastFire"
            options={LAST_FIRE_FILTER_OPTIONS}
          />
        </Box>
      )}
      <Box className={styles['table-container']}>
        <TableComponent
          title="Scheduled launches"
          headerActionButtons={[
            {
              name: 'Add',
              tooltip: 'Create a recurring schedule',
              icon: <PlusIcon className="icon" />,
              action: onCreateSchedule,
              section: 'left',
              contained: true,
            },
          ]}
          rows={filteredRows}
          columns={columns}
          loading={loading}
          isPagination={false}
          isData={filteredRows.length > 0}
          getRowClassName={(params) => {
            const tone = getScheduleLastFireRowTone(params.row.last_fire_outcome);
            if (tone === 'warning') return styles['schedule-row-warning'];
            if (tone === 'error') return styles['schedule-row-error'];
            return '';
          }}
          customNoData={(
            isFilteredEmpty
              ? <Typography variant="body2" color="text.secondary">No schedules match the current filters.</Typography>
              : <TestGroupSchedulesEmptyPanel onCreateSchedule={onCreateSchedule} />
          )}
        />
      </Box>
      <ConfirmationModal
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete schedule"
        message={
          deleteTarget
            ? `Are you sure you want to delete "${deleteTarget.name}"? It will stop firing. Fire history is kept for audit.`
            : ''
        }
        confirmButtonText="Delete"
        loading={deleting}
      />
    </Box>
  );
});

SchedulesTable.displayName = 'SchedulesTable';

export default SchedulesTable;
